import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import useDraft from "@/hooks/useDraft";
import MessageTemplatePicker from "@/components/MessageTemplatePicker";
import FilePicker from "@/components/FilePicker";
import { FormatBar } from "@/components/LinkifiedText";

/**
 * AdminThreadReplyBox — inline composer for an admin to reply to a specific
 * message thread. Posts to /admin/clients/{uid}/messages with
 * reply_to_message_id set so the message chains under the root. Draft state
 * is auto-saved via useDraft under `admin.threadreply.<rootId>`.
 */
export default function AdminThreadReplyBox({ userId, rootId, rootSubject, onSent, client }) {
  const [draft, setDraft, { saved, clear }] = useDraft(
    `admin.threadreply.${rootId}`, { body: "" }
  );
  const [busy, setBusy] = useState(false);
  const filesRef = useRef(null);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!draft.body.trim()) return;
    setBusy(true);
    try {
      const file_ids = [];
      for (const f of Array.from(filesRef.current?.files || [])) {
        const fd = new FormData();
        fd.append("file", f); fd.append("note", "Message attachment"); fd.append("tag", "message");
        const { data } = await api.post(`/admin/clients/${userId}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.file_id) file_ids.push(data.file_id);
      }
      const subject = `Re: ${(rootSubject || "your message").replace(/^Re:\s*/i, "")}`;
      const { data } = await api.post(`/admin/clients/${userId}/messages`, {
        subject,
        body: draft.body,
        reply_to_message_id: rootId,
        file_ids,
      });
      if (data?.warning) toast.warning(data.warning);
      else toast.success("Reply sent");
      clear();
      if (filesRef.current) filesRef.current.value = "";
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reply failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={send} className="mt-3 space-y-2" data-testid={`admin-thread-reply-${rootId}`}>
      <MessageTemplatePicker client={client} current={draft} bodyOnly testid={`thread-tpl-${rootId}`}
        onApply={(t) => setDraft({ ...draft, body: t.body })} />
      <textarea rows={3} value={draft.body}
        onChange={(e) => setDraft({ ...draft, body: e.target.value })}
        placeholder="Write a reply the client will receive via email…"
        data-testid={`admin-thread-reply-body-${rootId}`}
        className="w-full px-3 py-2 text-sm" />
      <FilePicker ref={filesRef} testid={`admin-thread-reply-files-${rootId}`} label="Attach files" />
      <div className="flex gap-2 items-center flex-wrap">
        <FormatBar value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })}
          target={`admin-thread-reply-body-${rootId}`} testid={`admin-thread-reply-fmt-${rootId}`} />
        <button type="submit" disabled={busy || !draft.body.trim()}
          data-testid={`admin-thread-reply-send-${rootId}`}
          className="btn-primary text-xs h-9">
          <PaperPlaneTilt size={11} weight="bold" />
          {busy ? "Sending…" : "Send reply"}
        </button>
        {saved && (
          <span className="label text-[10px]" style={{ color: "var(--text-dim)" }}>
            Draft saved
          </span>
        )}
        {draft.body && (
          <button type="button" onClick={clear}
            data-testid={`admin-thread-reply-clear-${rootId}`}
            className="btn-ghost text-[10px]">Clear</button>
        )}
      </div>
    </form>
  );
}
